<template>
  <div class="queue-table-card">
    <div class="queue-table-header">
      <h3>{{ t('Fila de Produção') }}</h3>
      <div class="queue-header-right">
        <span class="queue-count">{{ items.length }} {{ t('itens') }}</span>
        <span v-if="countdown != null" class="queue-countdown">
          <i class="pi pi-sync"></i> {{ countdown }}s
        </span>
        <Button
          v-if="countdown != null"
          icon="pi pi-refresh"
          size="small"
          severity="secondary"
          text
          rounded
          data-testid="production-queue-refresh-button"
          :aria-label="t('Atualizar agora')"
          @click="emit('refresh')"
        />
      </div>
    </div>

    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :data="decoratedItems"
      @row-click="(e: any) => handleRowClick(e)"
      @edit-click="handleEditClick"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';

type Id = number | string;

interface QueueItem {
  so_order_item_id?: Id;
  so_order_id?: Id;
  so_order_client_id?: Id;
  so_order_client_name?: string;
  cliente_ddi?: string;
  cliente_ddd?: string;
  cliente_telefone?: string;
  franchise_name?: string;
  so_service_name?: string;
  so_priority_name?: string;
  so_service_category_name?: string;
  item_description_value?: string;
  item_entry_photo_url?: string;
  so_order_item_qrcode?: string;
  current_step_name?: string;
  current_step_status?: string;
  completed_steps?: number | string;
  total_steps?: number | string;
  so_work_order_deadline_at?: string;
  so_order_item_expected_delivery_at?: string;
  urgency_score?: number;
  days_until_due?: number;
  [key: string]: unknown;
}

interface BadgeConfig {
  label: string;
  severity: string;
}

interface DataTableInstance {
  loadData?: () => void;
}

interface Props {
  items?: QueueItem[];
  selectedItemId?: number;
  countdown?: number;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  selectedItemId: undefined,
  countdown: undefined
});

const emit = defineEmits<{
  'select': [item: QueueItem];
  'open-order': [orderId: Id];
  'refresh': [];
}>();

const { t } = useI18n();
const datatableRef = ref<DataTableInstance | null>(null);

function urgencyBadge(score: number): { label: string; severity: string } {
  if (score >= 500) return { label: t('Atrasado pronto'), severity: 'danger' };
  if (score >= 400) return { label: t('Atrasado'),         severity: 'danger' };
  if (score >= 300) return { label: t('Avisar'),           severity: 'warning' };
  if (score >= 200) return { label: t('Aguardando cliente'), severity: 'info' };
  if (score >= 150) return { label: t('Próximo do prazo'),  severity: 'info' };
  return { label: t('No prazo'), severity: 'secondary' };
}

function daysUntilDueText(d: number | null | undefined): { text: string; severity: 'danger'|'warning'|'info'|'muted' } {
  if (d == null) return { text: '—', severity: 'muted' };
  if (d <  0)    return { text: `${-d} ${t('dia(s) atrasado')}`, severity: 'danger' };
  if (d === 0)   return { text: t('Hoje'), severity: 'warning' };
  if (d <= 2)    return { text: `${t('Em')} ${d} ${t('dia(s)')}`, severity: 'warning' };
  return            { text: `${t('Em')} ${d} ${t('dia(s)')}`, severity: 'info' };
}

const onlyDigits = (v?: string): string => String(v ?? '').replace(/\D/g, '');

const decoratedItems = computed(() =>
  props.items.map((r: QueueItem) => {
    const score = r.urgency_score ?? 0;
    const badge = urgencyBadge(score);
    const due = daysUntilDueText(r.days_until_due);
    // O número precisa existir como CAMPO da linha, não só dentro do href do
    // botão: a coluna WhatsApp filtra por `whatsapp`, e sem o campo o filtro
    // não tem contra o que casar.
    const phone = onlyDigits(r.cliente_telefone);
    return {
      ...r,
      whatsapp: phone
        ? `${onlyDigits(r.cliente_ddi)}${onlyDigits(r.cliente_ddd)}${phone}`
        : '',
      _os_label: `Ordem ID: ${r.so_order_id ?? ''}`,
      _so_order_item_label: `Item ID: ${r.so_order_item_id}`,
      _service_with_priority: r.so_priority_name
        ? `${r.so_service_name ?? ''} ${r.so_priority_name}`.trim()
        : (r.so_service_name ?? ''),
      _urgency_label: badge.label,
      _urgency_severity: badge.severity,
      _days_due_text: due.text,
      _days_due_severity: due.severity,
    };
  })
);

const tableColumns = computed(() => [
  {
    data: '_os_label',
    sortField: 'so_order_id',
    title: t('OS'),
    width: 110,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (_value: unknown, row?: Record<string, unknown>) => {
      emit('open-order', row?.so_order_id as Id);
    },
    mergedColumns: [
      {
        data: '_so_order_item_label',
        title: t('Item'),
        renderType: 'custom_clickable_id',
        clickCallback: (_value: unknown, row?: QueueItem) => {
          if (row) emit('select', row);
        },
      },
    ],
  },
  {
    data: 'item_entry_photo_url',
    title: t('Imagem'),
    width: 72,
    sortable: false,
    filterable: false,
    renderType: 'image',
    imageConfig: {
      width: 44,
      shape: 'rounded' as const,
      preview: true,
      emptyIcon: 'pi pi-image',
      alt: (row: Record<string, unknown>) => String(row.item_description_value ?? row.so_service_name ?? t('Item')),
    },
  },
  {
    data: 'so_order_client_name',
    title: t('Cliente'),
    width: 180,
    sortable: true,
    renderType: 'custom_clickable_id',
    clickCallback: (_value: unknown, row?: QueueItem) => {
      const clienteId = row?.so_order_client_id;
      if (clienteId) {
        eventBus.emit('modal.cliente.open', { id: clienteId });
      }
    },
    mergedColumns: [
      { data: 'franchise_name', title: t('Franquia') },
    ],
  },
  {
    data: 'whatsapp',
    title: t('WhatsApp'),
    width: 110,
    sortable: false,
    cellRenderer: (params: { data: Record<string, unknown> }): string => {
      if (params.data._isFrozenFooter) return '';
      const numero = String(params.data.whatsapp ?? '');
      if (!numero) return '';
      return `<a class="p-button p-button-success p-button-sm" href="https://api.whatsapp.com/send?phone=${numero}" target="_blank" title="${t('WhatsApp')}"><i class="pi pi-whatsapp"></i></a>`;
    },
  },
  {
    data: '_service_with_priority',
    title: t('Serviço'),
    width: 220,
    sortable: true,
    mergedColumns: [
      { data: 'so_service_category_name', title: t('Categoria') },
      { data: 'item_description_value', title: t('Descrição') },
    ],
  },
  {
    data: 'so_order_item_qrcode',
    title: t('QR Code'),
    width: 140,
    sortable: true,
    renderType: 'copy_on_click',
    filterType: 'qrcode'
  },
  {
    data: 'current_step_name',
    title: t('Passo Atual'),
    width: 150,
    sortable: true,
    renderType: 'badge',
    badgeConfig: (row: QueueItem): BadgeConfig => {
      const name = row.current_step_name || t('Sem etapa');
      const isActive = row.current_step_status === 'in_progress';
      return {
        label: isActive ? name : t('Aguardando') + ': ' + name,
        severity: isActive ? 'info' : 'warn'
      };
    }
  },
  {
    data: 'completed_steps',
    title: t('Progresso'),
    width: 140,
    sortable: false,
    cellRenderer: ({ data }: { data: QueueItem }): string => {
      const text = progressText(data);
      const pct = progressPercent(data);
      return `<div style="display:flex;flex-direction:column;gap:3px;">
        <span>${text}</span>
        <div style="width:100%;height:6px;border-radius:999px;background:var(--p-surface-200,#e5e7eb);overflow:hidden;">
          <div style="height:100%;width:${pct}%;border-radius:999px;background:${pct === 100 ? '#22c55e' : '#2563eb'};transition:width .3s;"></div>
        </div>
      </div>`;
    }
  },
  {
    data: '_urgency_label',
    sortField: 'urgency_score',
    title: t('Prazo'),
    width: 200,
    sortable: true,
    renderType: 'badge',
    badgeConfig: (row: Record<string, unknown>) => ({
      label: (row as { _urgency_label?: string })._urgency_label ?? '',
      severity: (row as { _urgency_severity?: string })._urgency_severity ?? 'secondary',
    }),
    mergedColumns: [
      {
        data: '_days_due_text',
        cellClass: (row: Record<string, unknown>) => `cell-days-${(row as { _days_due_severity?: string })._days_due_severity ?? 'muted'}`,
      },
      {
        data: 'so_order_item_expected_delivery_at',
        renderType: 'date',
      },
    ],
  }
]);

const datatableConfig = computed(() => ({
  tableId: 'productionQueueTable',
  // Sem isto o DataTable deduz a chave: procura uma coluna terminada em `_id`,
  // não acha nenhuma aqui, e cai na primeira coluna — `_os_label`, que é
  // "Ordem ID: X". Uma ordem com vários itens gera linhas com a MESMA chave,
  // o que quebra o diff do Vue e a seleção de linha. so_order_item_id é a
  // chave real de cada linha da fila.
  dataKey: 'so_order_item_id',
  columns: tableColumns.value,
  pagination: false,
  rowSelection: 'single',
  sortable: true,
  size: 'small',
  stripedRows: true,
  showGridlines: false,
  defaultSortField: 'urgency_score',
  defaultSortOrder: -1 as const,
  rowClass: (row: Record<string, unknown>) => {
    const s = (row as { urgency_score?: number }).urgency_score ?? 0;
    if (s >= 400) return 'urgency-row-danger';
    if (s >= 150) return 'urgency-row-warning';
    return '';
  },
}));

const progressText = (item: QueueItem): string => `${item.completed_steps || 0}/${item.total_steps || 0}`;

const progressPercent = (item: QueueItem): number => {
  const total = Number(item.total_steps || 0);
  const completed = Number(item.completed_steps || 0);
  if (!total) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
};

const handleRowClick = (row: QueueItem): void => {
  emit('select', row);
};

const handleEditClick = (itemId: Id): void => {
  const item = props.items.find((i: QueueItem) => i.so_order_item_id == itemId);
  if (item) {
    emit('select', item);
  }
};
</script>

<style scoped>
.queue-table-card {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 12px;
  padding: 1rem;
}

.queue-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.queue-table-header h3 {
  margin: 0;
}

.queue-header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.queue-countdown {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.45;
}

.queue-count {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.9rem;
}

.cell-days-danger   { color: var(--p-red-600,    #dc2626); font-weight: 500; }
.cell-days-warning  { color: var(--p-orange-600, #ea580c); font-weight: 500; }
.cell-days-info     { color: var(--p-blue-600,   #2563eb); }
.cell-days-muted    { color: var(--p-text-muted-color, #9ca3af); }

:deep(.urgency-row-danger  > td) { background: rgba(220, 38, 38, 0.06); }
:deep(.urgency-row-warning > td) { background: rgba(234, 88, 12, 0.06); }
</style>
